import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option, SelectController } from './select';
import { TextInputComponent } from './text';
import { FormComponent, FormComponentProps } from '../index';
import { useService } from '../../../services/services';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs/operators';
import FormField from '../../common/form-field';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { parseError } from '../../../services/utils';
import { useWatch } from 'react-hook-form';
import LegalEntity from '../../../interfaces/legal-entity';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import VidServiceResponseStatus from '../../../enums/vid-service-response-status';

export default function RegistrationNr(
  farmId: string,
  formActivityName: string,
  nameName: string,
  registrationNumberName: string,
  legalAddressName: string,
  callback: (form: UseFormReturn, data: any) => void,
  horizontal?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <RegistrationNrComponent
      farmId={farmId}
      formActivityName={formActivityName}
      nameName={nameName}
      registrationNumberName={registrationNumberName}
      legalAddressName={legalAddressName}
      callback={callback}
      horizontal={horizontal}
      {...rest}
    />
  );
}

export function RegistrationNrComponent({
  farmId,
  formActivityName,
  nameName,
  registrationNumberName,
  legalAddressName,
  readonly,
  disabled,
  required,
  callback,
  horizontal = false,
}: FormComponentProps & {
  farmId: string;
  formActivityName: string;
  nameName: string;
  registrationNumberName: string;
  legalAddressName: string;
  callback: (setValue: any, data: any) => void;
  horizontal?: boolean;
}) {
  const { t } = useTranslation();
  const { partnerService, vidService } = useService();
  const [nameDisabled, setNameDisabled] = useState(disabled);
  const [registrationNumberDisabled, setRegistrationNumberDisabled] =
    useState(disabled);

  const [nameLabelKey, setNameLabelKey] = useState(nameName);
  const [registrationNrLabelKey, setRegistrationNrLabelKey] = useState(
    registrationNumberName
  );
  const [msg, setMsg] = useState({ msg: null, type: null });
  const [formOfEconomicActivity, setFormOfEconomicActivity] = useState(null);
  const [mustValidateOnSRS, setMustValidateOnSRS] = useState(false);

  const {
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const formOfEconomicActivityError = parseError(formActivityName, errors);
  const nameError = parseError(nameName, errors);
  const registrationNumberError = parseError(registrationNumberName, errors);
  const [numberRequired, setNumberRequired] = useState(true);

  const watchSelect = useWatch({ name: formActivityName });
  const watchName = useWatch({ name: nameName });
  const watchRegistrationNumber = useWatch({ name: registrationNumberName });

  const [economicActivityForms] = useObservableState<Option[]>(
    () =>
      partnerService.formsOfEconomicActivity(farmId).pipe(
        map((values) =>
          values.map((value) => {
            return {
              value: value.id,
              label: value.value,
              properties: value.properties,
            };
          })
        )
      ),
    null
  );

  useEffect(() => {
    if (watchSelect && economicActivityForms) {
      setFormOfEconomicActivity(
        economicActivityForms.find((obj: any) => obj.value === watchSelect)
      );
    }
  }, [economicActivityForms, watchSelect]);

  useEffect(() => {
    if (formOfEconomicActivity) {
      setRegistrationNumberDisabled(false);

      setNameDisabled(
        Object.entries(formOfEconomicActivity.properties).find(
          ([key, obj]: any) =>
            obj.property.key === ClassifierPropertyKey.ALLOW_TO_TYPE_NAME &&
            obj.value === false
        )?.length > 0
      );

      const needPersonalCode =
        Object.entries(formOfEconomicActivity.properties).find(
          ([key, obj]: any) =>
            obj.property.key === ClassifierPropertyKey.NEED_PERSONAL_CODE &&
            obj.value === true
        )?.length > 0;

      setNameLabelKey(
        needPersonalCode ? 'pages.external.register.name' : 'name'
      );

      setRegistrationNrLabelKey(
        needPersonalCode
          ? 'pages.farm.users.personal_code'
          : 'registration_number'
      );

      setNumberRequired(
        Object.entries(formOfEconomicActivity.properties).find(
          ([key, obj]: any) =>
            obj.property.key ===
              ClassifierPropertyKey.REGISTRATION_NUMBER_REQUIRED &&
            obj.value === true
        )?.length > 0
      );

      setMustValidateOnSRS(
        Object.entries(formOfEconomicActivity.properties).find(
          ([key, obj]: any) =>
            obj.property.key ===
              ClassifierPropertyKey.VALIDATE_ON_STATE_REVENUE_SERVICE &&
            obj.value === true
        )?.length > 0
      );
    } else {
      setNameDisabled(true);
      setRegistrationNumberDisabled(true);
      setNumberRequired(true);
      setMustValidateOnSRS(false);
    }
  }, [formOfEconomicActivity]);

  useEffect(() => {
    setMsg({ msg: null, type: null });
    if (
      mustValidateOnSRS &&
      watchRegistrationNumber?.length === 11 &&
      !registrationNumberError?.message
    ) {
      vidService
        .validatePerson(getValues(registrationNumberName), getValues(nameName))
        .subscribe((response: LegalEntity) => {
          switch (response.status) {
            // @ts-expect-error
            case VidServiceResponseStatus.ok:
              setMsg({
                msg: 'pages.human.partners.data_found_on_SRS',
                type: 'success',
              });
              callback(setValue, response.data);
            // FALLS THROUGH to VidServiceResponseStatus.corrected
            case VidServiceResponseStatus.corrected:
              // In VID service physical person name has different format (SURNAME, NAME),
              // so in that case we do not update name
              if (response.data.person !== 'individual') {
                setValue(nameName, response.data.name);
                trigger(nameName);
              }
              break;
            case VidServiceResponseStatus.incomplete:
              break;
            case VidServiceResponseStatus.not_found:
              if (response.data !== null) {
                setMsg({
                  msg: 'No such company was found in VID register!',
                  type: 'error',
                });
              } else {
                setNameDisabled(false);
              }
              break;
            default:
              break;
          }
        });
    }
  }, [
    watchRegistrationNumber,
    watchSelect,
    watchName,
    farmId,
    getValues,
    legalAddressName,
    nameName,
    partnerService,
    registrationNumberName,
    setValue,
    trigger,
    callback,
    registrationNumberError?.message,
    mustValidateOnSRS,
    vidService,
  ]);

  return (
    <>
      <FormField
        label={t('classifiers.form_of_economic_activity')}
        labelFor={formActivityName}
        className={''}
        required={required?.includes(formActivityName)}
        error={formOfEconomicActivityError?.message ?? null}
        horizontal={horizontal}
      >
        <SelectController
          multi={false}
          disabled={disabled}
          asyncOptions={null}
          options={economicActivityForms ?? []}
          cacheOn={[]}
          name={formActivityName}
          parentName={null}
        />
      </FormField>
      <FormField
        label={t(nameLabelKey)}
        labelFor={nameName}
        required={required?.includes(nameName)}
        error={nameError?.message ?? null}
        horizontal={horizontal}
      >
        <TextInputComponent
          name={nameName}
          readonly={readonly}
          disabled={nameDisabled}
          icon={null}
        />
      </FormField>
      <FormField
        label={t(registrationNrLabelKey)}
        labelFor={registrationNumberName}
        required={numberRequired}
        error={
          registrationNumberError?.message ??
          (msg.type === 'error' ? msg.msg : null)
        }
        message={msg.type === 'success' ? msg.msg : null}
        horizontal={horizontal}
      >
        <TextInputComponent
          name={registrationNumberName}
          readonly={readonly}
          disabled={registrationNumberDisabled}
          icon={null}
        />
      </FormField>
    </>
  );
}
